import React, { useState, useRef, useEffect } from 'react';
import { getAvatarUrl } from '../utils/avatar-utils';

const SafeAvatar = ({ 
  src, 
  alt = "Profile", 
  className = "w-8 h-8 rounded-full", 
  fallbackSrc = "https://placehold.co/32x32",
  size = 32,
  lazy = true 
}) => {
  // Convert S3 keys to full URLs using the backend endpoint
  const resolvedSrc = src ? getAvatarUrl(src) : null;

  const [imageSrc, setImageSrc] = useState(() => {
    if (lazy) return null;
    return resolvedSrc;
  });
  const [isLoading, setIsLoading] = useState(lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || !resolvedSrc) {
      setImageSrc(resolvedSrc);
      setIsLoading(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageSrc) {
            setImageSrc(resolvedSrc);
            setIsLoading(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [resolvedSrc, lazy, imageSrc]);

  // Update image src when resolved src changes
  useEffect(() => {
    if (resolvedSrc && resolvedSrc !== imageSrc && !lazy) {
      setImageSrc(resolvedSrc);
      setHasError(false);
    }
  }, [resolvedSrc, imageSrc, lazy]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // If lazy loading and no src set yet, show placeholder
  if (lazy && !imageSrc) {
    return (
      <div 
        ref={imgRef}
        className={`${className} bg-gray-200 flex items-center justify-center`}
        style={{ aspectRatio: '1 / 1' }}
      >
        {isLoading ? (
          <div className="animate-pulse bg-gray-300 w-full h-full rounded-full"></div>
        ) : (
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        )}
      </div>
    );
  }

  // If we have an error, show placeholder
  if (hasError) {
    return (
      <div 
        className={`${className} bg-gray-200 flex items-center justify-center`}
        style={{ aspectRatio: '1 / 1' }}
      >
        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`${className} bg-gray-100 overflow-hidden relative`} style={{ aspectRatio: '1 / 1' }}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-full"></div>
      )}
      
      <img
        ref={imgRef}
        src={imageSrc || fallbackSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        style={{ 
          aspectRatio: '1 / 1',
          objectFit: 'cover'
        }}
      />
    </div>
  );
};

export default SafeAvatar; 